/**
 * Berlin's 96 Ortsteile, and the Bezirk (borough) each belongs to.
 *
 * WP 3.4c. Dependency-free by design (CLAUDE.md's testable-rules table), so
 * `test/districts.test.ts` runs from the Worker's own suite and `leadVenue.ts`
 * can import it without pulling in anything else.
 *
 * ## The collision this file has to resolve
 *
 * Six Ortsteile share their name with the Bezirk they sit in — `Mitte`,
 * `Neukölln`, `Pankow`, `Reinickendorf`, `Lichtenberg` (all in the live data)
 * and `Spandau` (not, yet). One string, two possible readings: rule 1 of
 * CLAUDE.md's bug list.
 *
 * The rule here: **an Ortsteil match wins**. A bare `"Mitte"` becomes
 * `level: 'ortsteil'`, because a source writing the bare name of a place this
 * small and this central is almost certainly naming the neighbourhood, not
 * filing a return address at the borough. `toOrtsteil` only reports
 * `'bezirk'` when the string does not match any of the 96 — the six merged,
 * hyphenated Bezirke (`Friedrichshain-Kreuzberg`, `Charlottenburg-Wilmersdorf`,
 * `Steglitz-Zehlendorf`, `Tempelhof-Schöneberg`, `Treptow-Köpenick`,
 * `Marzahn-Hellersdorf`) never collide, because none of them is also an
 * Ortsteil name — or when the source itself wrote `"Bezirk <name>"`, which is
 * treated as a deliberate signal and never downgraded to a guess.
 *
 * This is a single rule applied uniformly, written here once rather than
 * decided differently value by value — the file this codebase keeps writing
 * a bug over is the one where two similar-looking cases get answered two
 * different ways and nobody wrote down why.
 *
 * ## `level: 'outside'` is two things, on purpose, documented rather than hidden
 *
 * The interface only has three levels, and Postgres and the harvest agree on
 * this file, not a fourth case. `'outside'` covers a real Potsdam Stadtteil
 * (`ortsteil: 'Potsdam'`, `bezirk: null`) and a value this table cannot place
 * at all — a name straddling an Ortsteil boundary, an unrelated city, or
 * anything unrecognised (`ortsteil: null`, `bezirk: null`). A caller that
 * needs to tell "known non-Berlin" from "unresolved" reads `ortsteil` too;
 * nothing in this WP needs that distinction to branch on `level` alone.
 */

/** Ortsteil -> Bezirk, all 96. Keys are the canonical, correctly-cased
 *  Ortsteil name; `toOrtsteil` does the case-insensitive matching. */
export const ORTSTEILE: Record<string, string> = {
  // Mitte
  Mitte: 'Mitte',
  Moabit: 'Mitte',
  Hansaviertel: 'Mitte',
  Tiergarten: 'Mitte',
  Wedding: 'Mitte',
  Gesundbrunnen: 'Mitte',
  // Friedrichshain-Kreuzberg
  Friedrichshain: 'Friedrichshain-Kreuzberg',
  Kreuzberg: 'Friedrichshain-Kreuzberg',
  // Pankow
  Blankenburg: 'Pankow',
  Blankenfelde: 'Pankow',
  Buch: 'Pankow',
  'Französisch Buchholz': 'Pankow',
  Heinersdorf: 'Pankow',
  Karow: 'Pankow',
  Niederschönhausen: 'Pankow',
  Pankow: 'Pankow',
  'Prenzlauer Berg': 'Pankow',
  Rosenthal: 'Pankow',
  'Stadtrandsiedlung Malchow': 'Pankow',
  Weißensee: 'Pankow',
  Wilhelmsruh: 'Pankow',
  // Charlottenburg-Wilmersdorf
  Charlottenburg: 'Charlottenburg-Wilmersdorf',
  Grunewald: 'Charlottenburg-Wilmersdorf',
  Halensee: 'Charlottenburg-Wilmersdorf',
  Schmargendorf: 'Charlottenburg-Wilmersdorf',
  Westend: 'Charlottenburg-Wilmersdorf',
  Wilmersdorf: 'Charlottenburg-Wilmersdorf',
  // Spandau
  Spandau: 'Spandau',
  Haselhorst: 'Spandau',
  Siemensstadt: 'Spandau',
  Staaken: 'Spandau',
  Gatow: 'Spandau',
  Kladow: 'Spandau',
  Hakenfelde: 'Spandau',
  'Falkenhagener Feld': 'Spandau',
  Wilhelmstadt: 'Spandau',
  // Steglitz-Zehlendorf
  Dahlem: 'Steglitz-Zehlendorf',
  Lankwitz: 'Steglitz-Zehlendorf',
  Lichterfelde: 'Steglitz-Zehlendorf',
  Nikolassee: 'Steglitz-Zehlendorf',
  Schlachtensee: 'Steglitz-Zehlendorf',
  Steglitz: 'Steglitz-Zehlendorf',
  Wannsee: 'Steglitz-Zehlendorf',
  Zehlendorf: 'Steglitz-Zehlendorf',
  // Tempelhof-Schöneberg
  Friedenau: 'Tempelhof-Schöneberg',
  Lichtenrade: 'Tempelhof-Schöneberg',
  Mariendorf: 'Tempelhof-Schöneberg',
  Marienfelde: 'Tempelhof-Schöneberg',
  Schöneberg: 'Tempelhof-Schöneberg',
  Tempelhof: 'Tempelhof-Schöneberg',
  // Neukölln
  Britz: 'Neukölln',
  Buckow: 'Neukölln',
  Gropiusstadt: 'Neukölln',
  Neukölln: 'Neukölln',
  Rudow: 'Neukölln',
  // Treptow-Köpenick
  Adlershof: 'Treptow-Köpenick',
  Altglienicke: 'Treptow-Köpenick',
  'Alt-Treptow': 'Treptow-Köpenick',
  Baumschulenweg: 'Treptow-Köpenick',
  Bohnsdorf: 'Treptow-Köpenick',
  Friedrichshagen: 'Treptow-Köpenick',
  Grünau: 'Treptow-Köpenick',
  Johannisthal: 'Treptow-Köpenick',
  Köpenick: 'Treptow-Köpenick',
  Müggelheim: 'Treptow-Köpenick',
  Niederschöneweide: 'Treptow-Köpenick',
  Oberschöneweide: 'Treptow-Köpenick',
  Plänterwald: 'Treptow-Köpenick',
  Rahnsdorf: 'Treptow-Köpenick',
  Schmöckwitz: 'Treptow-Köpenick',
  // Marzahn-Hellersdorf
  Biesdorf: 'Marzahn-Hellersdorf',
  Hellersdorf: 'Marzahn-Hellersdorf',
  Kaulsdorf: 'Marzahn-Hellersdorf',
  Mahlsdorf: 'Marzahn-Hellersdorf',
  Marzahn: 'Marzahn-Hellersdorf',
  // Lichtenberg
  'Alt-Hohenschönhausen': 'Lichtenberg',
  Falkenberg: 'Lichtenberg',
  Fennpfuhl: 'Lichtenberg',
  Friedrichsfelde: 'Lichtenberg',
  Karlshorst: 'Lichtenberg',
  Lichtenberg: 'Lichtenberg',
  Malchow: 'Lichtenberg',
  'Neu-Hohenschönhausen': 'Lichtenberg',
  Rummelsburg: 'Lichtenberg',
  Wartenberg: 'Lichtenberg',
  // Reinickendorf
  Borsigwalde: 'Reinickendorf',
  Frohnau: 'Reinickendorf',
  Heiligensee: 'Reinickendorf',
  Hermsdorf: 'Reinickendorf',
  Konradshöhe: 'Reinickendorf',
  Lübars: 'Reinickendorf',
  'Märkisches Viertel': 'Reinickendorf',
  Reinickendorf: 'Reinickendorf',
  Tegel: 'Reinickendorf',
  Waidmannslust: 'Reinickendorf',
  Wittenau: 'Reinickendorf',
}

/** The 12 Bezirke, read off `ORTSTEILE`'s own values rather than listed a
 *  second time — a second list is a second place for the count to drift.
 *  Order is first-appearance in `ORTSTEILE` above, which is already a
 *  sensible, stable one for a chip row. */
export const BEZIRKE: string[] = [...new Set(Object.values(ORTSTEILE))]

const ORTSTEIL_BY_LOWER = new Map(Object.keys(ORTSTEILE).map(k => [k.toLowerCase(), k]))
const BEZIRK_BY_LOWER = new Map(BEZIRKE.map(b => [b.toLowerCase(), b]))

/** Potsdam Stadtteile seen in `venues.district` that do not contain the
 *  string "Potsdam" — CLAUDE.md's own trap ("a site's own sitemap can be an
 *  archive") in miniature: the obvious substring check would have missed
 *  every one of these. Extend this list from a live query, never guess. */
const POTSDAM_STADTTEILE = new Set([
  'historische innenstadt',
  'brandenburger vorstadt',
  'brauhausberg',
])

/** Historic quarters that straddle an Ortsteil boundary. `Luisenstadt` is
 *  partly Mitte, partly Kreuzberg; picking either would assert a location the
 *  name itself does not have. Mapped to null rather than guessed, per this
 *  file's own stop condition. */
const UNRESOLVABLE = new Set(['luisenstadt', 'rosenthaler vorstadt'])

export interface DistrictLookup {
  ortsteil: string | null
  bezirk: string | null
  level: 'ortsteil' | 'bezirk' | 'outside'
}

/**
 * Normalise a free-text `venues.district` value.
 *
 * Total over every live value (see `test/districts.test.ts`): every one of
 * the 57 non-null values in `venues.district` as of 2026-09-26 resolves to a
 * level other than a silent guess, and an unrecognised string maps to null
 * rather than being forced into one.
 */
export function toOrtsteil(raw: string | null): DistrictLookup {
  const s = raw?.trim()
  if (!s) return { ortsteil: null, bezirk: null, level: 'outside' }
  const lower = s.toLowerCase()

  // A source that wrote "Bezirk <name>" said so on purpose — respected
  // ahead of the Ortsteil table even when the stripped name is also an
  // Ortsteil (e.g. "Bezirk Mitte"), because an explicit label is not a case
  // for this file's own default-to-Ortsteil rule to override.
  if (lower.startsWith('bezirk ')) {
    const name = BEZIRK_BY_LOWER.get(lower.slice('bezirk '.length).trim())
    if (name) return { ortsteil: name, bezirk: name, level: 'bezirk' }
  }

  if (lower.startsWith('berlin-')) {
    const name = ORTSTEIL_BY_LOWER.get(lower.slice('berlin-'.length).trim())
    if (name) return { ortsteil: name, bezirk: ORTSTEILE[name], level: 'ortsteil' }
  }

  const ortsteilName = ORTSTEIL_BY_LOWER.get(lower)
  if (ortsteilName) return { ortsteil: ortsteilName, bezirk: ORTSTEILE[ortsteilName], level: 'ortsteil' }

  const bezirkName = BEZIRK_BY_LOWER.get(lower)
  if (bezirkName) return { ortsteil: bezirkName, bezirk: bezirkName, level: 'bezirk' }

  if (lower === 'potsdam' || POTSDAM_STADTTEILE.has(lower)) {
    return { ortsteil: 'Potsdam', bezirk: null, level: 'outside' }
  }

  if (UNRESOLVABLE.has(lower)) return { ortsteil: null, bezirk: null, level: 'outside' }

  // Includes "Dessau" and anything this table has never seen — the stop
  // condition's "map it to null" case, not a guess.
  return { ortsteil: null, bezirk: null, level: 'outside' }
}
